import BaseListPresenter from "../../../../base/BaseListPresenter";
import { PrintPayrollListing } from "./print/PrintPayrollListing";
import { _getCasesType, caseFieldMap } from "./payrollCases/PayrollCasesPage";
import * as XLSX from "xlsx";
import {
  _labelKeyPairs,
  _getHeaders,
  _getLabels,
  _mergeDynamicKeys,
  _processExportData,
  _getCaseHandler,
  _parseKeyValueArrayToObject,
  _getNumericSum,
  _expandEmployeeNameColumns,
  _withEarnedPeriodExportLabels,
} from "./utils/helperFunction";

class PayrollTablePresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;
    this.view = view;
  }
  init() {
    const object = this.view._getViewProps();

    this.limit = 500;
    this.where = { payrollID: object.id };
    this.search = {};
    this.filter = {};
    this.include = ["all"];
    this.keys = undefined; // if keys are specified, only those keys will be returned
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  createQuery() {
    const skip = (this.current - 1) * this.limit;

    const query = {
      limit: this.limit,
      // skip: skip,
      where: { ...this.where, ...this.search, ...this.filter },
      include: this.include,
    };
    if (this.sort) {
      query.sort = this.sort;
    }
    const keys = this.keys || this.view.getKeys() || [];
    if (keys.length > 0) {
      query.keys = keys;
    }
    return query;
  }

  async findObjects() {
    const collection = this.view.getCollectionName();
    const query = this.createQuery();
    const propObject = this.view._getViewProps();

    try {
      this.showProgress();
      this.findObjectUseCase.abort();
      const objects = await this.findObjectUseCase.execute(collection, query);
      const tableObjects = this._getTableObjects(objects, propObject);
      const tfootObjects = this._getTfootObjects(tableObjects, propObject);

      this.objects = this.objects.concat(objects);
      this.view.setObjects(objects);
      this.view.setState((prevState) => ({
        panelState: {
          ...prevState.panelState,
          tableObjects,
          tfootObjects,
        },
      }));
      this.view.setCurrent(this.current);
      this.hideProgress();
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
    this.progress = false;
  }

  setCurrentPage(page) {
    this.current = page;
    this.documents = [];
    this.view.setCurrent(page);
    this.view.setObjects([]);
    this.findObjects();
  }

  _getActiveCase(objects) {
    const cases = _getCasesType(objects);
    return Object.keys(cases).find((key) => cases[key]);
  }

  _getTableObjects(objects, propObject) {
    const activeCase = this._getActiveCase(propObject);

    const result = [];

    for (let i = 0; i < objects.length; i++) {
      const obj = objects[i];

      const {
        surname = "",
        Firstname = "",
        Middlename = "",
        agency,
        accountNumber,
      } = obj?.employee;

      let formattedObject = {
        id: obj?.id,
        basic: obj?.oldRate,
        pera: obj?.peraAda,
        netPay: obj?.netAmount,
        ln: surname,
        fn: Firstname,
        mn: Middlename,
        employeeNumber: agency,
        accountNumber,
        acctNo: accountNumber,
      };

      const caseFields = _getCaseHandler(activeCase, obj);

      Object.assign(formattedObject, caseFields);

      _parseKeyValueArrayToObject(formattedObject, obj?.contributions);
      _parseKeyValueArrayToObject(formattedObject, obj?.deductions);

      result.push(formattedObject);
    }
    return result;
  }

  _getTfootObjects(objects, propObject) {
    if (!objects.length) return [];

    const activeCase = this._getActiveCase(propObject);

    const fieldsToExclude = new Set(caseFieldMap[activeCase] || []);
    const allKeys = [...new Set(objects.flatMap((obj) => Object.keys(obj)))];
    const fieldsToSum = allKeys.filter(
      (key) => !fieldsToExclude.has(key) && key !== ""
    );

    const totals = {};

    for (const key of fieldsToSum) {
      const keyTotal = objects.reduce(
        (acc, row) => acc + _getNumericSum(row[key]),
        0
      );
      if (keyTotal !== 0) {
        totals[key] = Math.round(keyTotal * 100) / 100;
      }
    }
    return totals;
  }

  _handlePrintData() {
    const { tableProps, tableObjects, tfootObjects } =
      this.view.state.panelState;
    const { signatories, payrollHeader } = this.view.props.formData;
    const { startDate, endDate, payrollCode, jobType, payItem } = this.view.props.object;

    const headerData = {
      startDate,
      endDate,
      payrollCode,
    }

    const params = {
      object: tableObjects,
      headers: tableProps,
      tfootObjects,
      otherData: {
        selectedTypes: {
          jobTypeVal: jobType,
          payItems: payItem,
        },
      },
      signatories,
      headerData,
      payrollHeader,
    };

    PrintPayrollListing(params);
  }

  _handleExportData({ objects, fieldsToExclude, object }) {
    const schema = this.view.getSchema(this.view.getCollectionName());
    const fileName = `${objects[0]?.payrollType}_${objects[0]?.jobType}_${objects[0]?.payItem}`;

    let label = _expandEmployeeNameColumns(
      _getLabels(schema, fieldsToExclude),
      this.view.isJoItem?.() ?? object?.jobType === "Job Order"
    );

    const contributionKeys = _labelKeyPairs(object?.contributions);
    const deductionKeys = _labelKeyPairs(object?.deductions);
    const incentiveKeys = _labelKeyPairs(object?.incentives);

    const params = {
      objects,
      label,
      object,
      contributionKeys,
      deductionKeys,
      incentiveKeys,
    };

    label = _mergeDynamicKeys(params);
    label = _withEarnedPeriodExportLabels(label, object);
    params.label = label;
    const processedData = _processExportData(params);

    const { headerRow_1, headerRow_2, merges } = _getHeaders(params);

    const worksheet = XLSX.utils.aoa_to_sheet([
      headerRow_1,
      headerRow_2,
      ...processedData.map((row) => label.map(({ label }) => row[label] ?? "")),
    ]);

    worksheet["!merges"] = merges;

    const colWidths = label.map(({ label: header }) => {
      const maxWidth = Math.max(
        header.length,
        ...processedData.map((row) => String(row[header] || "").length)
      );
      return { wch: Math.min(Math.max(maxWidth + 2, 10), 50) };
    });
    worksheet["!cols"] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Export Data");
    XLSX.writeFile(workbook, `${fileName}.xlsx`, { cellStyles: true });
  }
}
export default PayrollTablePresenter;
